/**
 * Dedicated RingCentral DataSource panel — single place to manage RC accounts,
 * inspect call logs, run on-demand recording processing, and dry-run the next
 * ETL batch. Mirrors the structural conventions of TelegramDedicatedPanel and
 * talks only to /v1/ringcentral.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    Input,
    LinearProgress,
    Modal,
    ModalClose,
    ModalDialog,
    Slider,
    Sheet,
    Stack,
    Tooltip,
    Typography,
} from '@mui/joy'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk'
import PhoneCallbackIcon from '@mui/icons-material/PhoneCallback'
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded'
import RefreshIcon from '@mui/icons-material/Refresh'
import ScienceIcon from '@mui/icons-material/Science'
import SearchIcon from '@mui/icons-material/Search'
import SyncIcon from '@mui/icons-material/Sync'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import ReplayIcon from '@mui/icons-material/Replay'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { EtlPreviewModal } from '../shared/EtlPreviewModal'
import VisibilityIcon from '@mui/icons-material/Visibility'
import MicIcon from '@mui/icons-material/Mic'
import HeadphonesIcon from '@mui/icons-material/Headphones'
import PauseIcon from '@mui/icons-material/Pause'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'

import type { RingCentralAccount, RingCentralRecording } from '@numeo/types'
import { decodeToken } from 'setup/SetupAxios'
import * as rcApi from '../../../data-sources/ds-ringcentral/api'
import {
    type RecordingStatus,
    type RingCentralAccountState,
    type RingCentralStatusCounts,
    type RingCentralSourceBatch,
} from '../../../data-sources/ds-ringcentral/api'
import { useUrlPathSegment } from '../../hooks/useUrlPathSegment'

// ─── helpers ─────────────────────────────────────────────────────────────────

function workspaceId(): string {
    return decodeToken()?.projectName ?? 'default'
}

function fmtDate(iso: string | undefined | null): string {
    if (!iso) return '—'
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function fmtDuration(seconds: number | undefined): string {
    if (!seconds || seconds < 0) return '—'
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}:${String(s).padStart(2, '0')}`
}

function relTime(iso: string | null | undefined): string {
    if (!iso) return '—'
    const ms = Date.now() - new Date(iso).getTime()
    if (!Number.isFinite(ms) || ms < 0) return '—'
    const sec = Math.floor(ms / 1000)
    if (sec < 60) return `${sec}s ago`
    const m = Math.floor(sec / 60)
    if (m < 60) return `${m}m ago`
    const h = Math.floor(m / 60)
    if (h < 24) return `${h}h ago`
    return `${Math.floor(h / 24)}d ago`
}

function tsToIso(v: RingCentralAccountState['last_poll_at']): string | undefined {
    if (!v) return undefined
    if (typeof v === 'string') return v
    if (typeof v === 'object' && '_seconds' in v) return new Date(v._seconds * 1000).toISOString()
    return undefined
}

type JoyColor = 'neutral' | 'primary' | 'success' | 'warning' | 'danger'

function recordingStatusColor(status: RecordingStatus): JoyColor {
    switch (status) {
        case 'discovered':
            return 'neutral'
        case 'transcribing':
        case 'transcribed':
        case 'diarizing':
            return 'primary'
        case 'ready':
            return 'success'
        case 'pending':
            return 'warning'
        case 'processed':
            return 'success'
        case 'unattributed':
            return 'warning'
        case 'failed':
            return 'danger'
    }
}

function accountStatusColor(s: RingCentralAccount['status']): JoyColor {
    return s === 'active' ? 'success' : s === 'oauth_expired' ? 'warning' : 'danger'
}

function accountStatusLabel(s: RingCentralAccount['status']): string {
    return s === 'active' ? 'active' : s === 'oauth_expired' ? 'token expired' : 'disconnected'
}

// ─── small reusable bits ─────────────────────────────────────────────────────

function StatusChip({ status }: { status: RecordingStatus }): JSX.Element {
    return (
        <Chip size="sm" variant="soft" color={recordingStatusColor(status)}>
            {status}
        </Chip>
    )
}

function AccountStatusChip({ status }: { status: RingCentralAccount['status'] }): JSX.Element {
    return (
        <Chip size="sm" variant="soft" color={accountStatusColor(status)}>
            {accountStatusLabel(status)}
        </Chip>
    )
}

function FactItem({ label, value, danger }: { label: string; value: string; danger?: boolean }): JSX.Element {
    return (
        <Stack direction="row" gap={0.5} alignItems="baseline">
            <Typography level="body-xs" color="neutral" sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {label}
            </Typography>
            <Typography level="body-xs" color={danger ? 'danger' : undefined} sx={{ fontWeight: 'md' }}>
                {value}
            </Typography>
        </Stack>
    )
}

function DirectionIcon({ direction }: { direction: 'Inbound' | 'Outbound' }): JSX.Element {
    return direction === 'Inbound' ? (
        <PhoneCallbackIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
    ) : (
        <PhoneForwardedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
    )
}

// ─── accounts list view (default) ────────────────────────────────────────────

type AccountFilter = 'all' | 'active' | 'oauth_expired' | 'disconnected'
type AccountSort = 'name' | 'lastSynced'

interface AggregateCounts {
    pending: number
    processed: number
    unattributed: number
    failed: number
}

function AccountsListView({
    accounts,
    loading,
    onOpenAccount,
    onConnect,
    connecting,
}: {
    accounts: RingCentralAccount[]
    loading: boolean
    onOpenAccount: (accountId: string) => void
    onConnect: () => void
    connecting: boolean
}): JSX.Element {
    const [filter, setFilter] = useState<AccountFilter>('all')
    const [sort, setSort] = useState<AccountSort>('lastSynced')
    const [search, setSearch] = useState('')

    const counts = useMemo(() => {
        const c: Record<AccountFilter, number> = { all: accounts.length, active: 0, oauth_expired: 0, disconnected: 0 }
        for (const a of accounts) c[a.status]++
        return c
    }, [accounts])

    const visible = useMemo(() => {
        let list = filter === 'all' ? accounts.slice() : accounts.filter((a) => a.status === filter)
        if (search.trim()) {
            const q = search.trim().toLowerCase()
            list = list.filter((a) => a.account_name.toLowerCase().includes(q) || a.account_id.toLowerCase().includes(q))
        }
        list.sort((a, b) => {
            if (sort === 'name') return a.account_name.localeCompare(b.account_name)
            return (Date.parse(b.last_synced_at ?? '0') || 0) - (Date.parse(a.last_synced_at ?? '0') || 0)
        })
        return list
    }, [accounts, filter, search, sort])

    return (
        <Stack gap={1.5}>
            {/* accounts card */}
            <Sheet variant="outlined" sx={{ borderRadius: 'md', p: 1.5 }}>
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
                    <Typography level="title-sm">Accounts ({counts.all})</Typography>
                    <Box sx={{ flex: 1 }} />
                    <Input
                        size="sm"
                        startDecorator={<SearchIcon sx={{ fontSize: 14 }} />}
                        placeholder="Search…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ width: 200 }}
                    />
                    <Typography level="body-xs" color="neutral">
                        Sort:
                    </Typography>
                    {(['lastSynced', 'name'] as const).map((s) => (
                        <Chip key={s} size="sm" variant={sort === s ? 'solid' : 'soft'} color="neutral" onClick={() => setSort(s)} sx={{ cursor: 'pointer' }}>
                            {s === 'lastSynced' ? 'Last synced' : 'Name'}
                        </Chip>
                    ))}
                </Stack>

                <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ mb: 1.5 }}>
                    {(['all', 'active', 'oauth_expired', 'disconnected'] as const).map((f) => (
                        <Chip
                            key={f}
                            size="sm"
                            variant={filter === f ? 'solid' : 'soft'}
                            color={f === 'all' ? 'neutral' : f === 'active' ? 'success' : f === 'oauth_expired' ? 'warning' : 'danger'}
                            onClick={() => setFilter(f)}
                            sx={{ cursor: 'pointer' }}
                        >
                            {f === 'oauth_expired' ? 'token expired' : f} · {counts[f]}
                        </Chip>
                    ))}
                    <Box sx={{ flex: 1 }} />
                    <Button
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        startDecorator={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                        loading={connecting}
                        onClick={onConnect}
                    >
                        {accounts.length === 0 ? 'Connect RingCentral' : 'Add another account'}
                    </Button>
                </Stack>

                {loading && accounts.length === 0 ? (
                    <Stack alignItems="center" py={3}>
                        <CircularProgress size="sm" />
                    </Stack>
                ) : visible.length === 0 ? (
                    <Sheet variant="soft" sx={{ p: 3, borderRadius: 'sm', textAlign: 'center' }}>
                        <Typography level="body-sm" color="neutral">
                            {accounts.length === 0
                                ? 'No RingCentral accounts connected. Click “Connect RingCentral” above to authorize one.'
                                : 'No accounts match this filter.'}
                        </Typography>
                    </Sheet>
                ) : (
                    <Stack gap={0.75}>
                        {visible.map((a) => (
                            <AccountRow
                                key={a.account_id}
                                account={a}
                                onOpen={() => {
                                    // eslint-disable-next-line no-console
                                    console.log('[RingCentralPanel] AccountRow click', {
                                        account_id: a.account_id,
                                        type: typeof a.account_id,
                                        name: a.account_name,
                                    })
                                    onOpenAccount(String(a.account_id))
                                }}
                            />
                        ))}
                    </Stack>
                )}
            </Sheet>
        </Stack>
    )
}

function AccountRow({ account, onOpen }: { account: RingCentralAccount; onOpen: () => void }): JSX.Element {
    return (
        <Sheet
            variant="outlined"
            onClick={onOpen}
            sx={{
                borderRadius: 'sm',
                p: 1.25,
                cursor: 'pointer',
                transition: 'background-color 120ms',
                '&:hover': { bgcolor: 'background.level1' },
            }}
        >
            <Stack direction="row" alignItems="center" gap={1.5}>
                <Sheet
                    variant="soft"
                    color={accountStatusColor(account.status)}
                    sx={{ borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                >
                    <PhoneInTalkIcon sx={{ fontSize: 16 }} />
                </Sheet>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                        <AccountStatusChip status={account.status} />
                        <Typography level="title-sm" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {account.account_name || `Account #${account.account_id}`}
                        </Typography>
                    </Stack>
                    <Stack direction="row" gap={1.5} flexWrap="wrap">
                        <FactItem label="Last synced" value={relTime(account.last_synced_at)} />
                        {account.connected_at && <FactItem label="Connected" value={relTime(account.connected_at)} />}
                        {account.last_error && <FactItem label="Error" value={account.last_error} danger />}
                    </Stack>
                </Box>
                <Typography level="h4" sx={{ color: 'text.tertiary', lineHeight: 1 }}>
                    ›
                </Typography>
            </Stack>
        </Sheet>
    )
}

// ─── account detail (flat page, TG-style) ────────────────────────────────────

const ALL_RECORDING_STATUSES: RecordingStatus[] = [
    'discovered',
    'transcribing',
    'transcribed',
    'diarizing',
    'ready',
    'pending',
    'processed',
    'unattributed',
    'failed',
]

type RecordingStatusFilter = RecordingStatus | 'all'

function AccountDetailView({
    account,
    onBack,
    onConnect,
    connecting,
    onDisconnect,
    onFetch,
    fetching,
}: {
    account: RingCentralAccount
    onBack: () => void
    onConnect: () => void
    connecting: boolean
    onDisconnect: (accountId: string) => Promise<void>
    onFetch: (accountId: string) => Promise<void>
    fetching: boolean
}): JSX.Element {
    const PAGE_SIZE = 30
    const [recordings, setRecordings] = useState<RingCentralRecording[]>([])
    const [stats, setStats] = useState<rcApi.RingCentralAccountStats | null>(null)
    const [stateDoc, setStateDoc] = useState<RingCentralAccountState | null>(null)
    const [filter, setFilter] = useState<RecordingStatusFilter>('all')
    const [page, setPage] = useState(0)
    const [loading, setLoading] = useState(false)
    const [busyId, setBusyId] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [selected, setSelected] = useState<RingCentralRecording | null>(null)

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const [recs, statsRes, st] = await Promise.all([
                rcApi.listRecordings(account.account_id, undefined, 500),
                rcApi.getAccountStats(account.account_id),
                rcApi.getAccountState(workspaceId(), account.account_id),
            ])
            setRecordings(recs)
            setStats(statsRes)
            setStateDoc(st)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load recordings')
        } finally {
            setLoading(false)
        }
    }, [account.account_id])

    useEffect(() => {
        setPage(0)
        load()
    }, [load])

    useEffect(() => {
        setPage(0)
    }, [filter])

    const filtered = useMemo(() => (filter === 'all' ? recordings : recordings.filter((r) => r.status === filter)), [recordings, filter])
    const visiblePage = useMemo(() => filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE), [filtered, page])
    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
    const counts = stats?.counts ?? null

    const lastPollIso = tsToIso(stateDoc?.last_poll_at)

    const handleAction = useCallback(
        async (action: 'process' | 'force' | 'requeue' | 'delete', id: string) => {
            setBusyId(id)
            setError(null)
            try {
                if (action === 'process' || action === 'force') {
                    const result = await rcApi.processRecording(id, { force: action === 'force' })
                } else if (action === 'requeue') {
                    await rcApi.requeueRecording(id)
                } else if (action === 'delete') {
                    if (!window.confirm('Delete this recording? GCS audio will also be removed. This cannot be undone.')) {
                        setBusyId(null)
                        return
                    }
                    await rcApi.deleteRecording(id)
                    if (selected?.recording_id === id) setSelected(null)
                }
                await load()
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Action failed')
            } finally {
                setBusyId(null)
            }
        },
        [load, selected?.recording_id]
    )

    return (
        <Sheet variant="outlined" sx={{ borderRadius: 'md', p: 2 }}>
            <Stack gap={1.5}>
                {/* header */}
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <IconButton size="sm" variant="plain" onClick={onBack}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Sheet
                        variant="soft"
                        color={accountStatusColor(account.status)}
                        sx={{ borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                    >
                        <PhoneInTalkIcon sx={{ fontSize: 16 }} />
                    </Sheet>
                    <Typography level="title-md" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {account.account_name || `Account #${account.account_id}`}
                    </Typography>
                    <AccountStatusChip status={account.status} />
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title="Pulls call-log records with recordings from RingCentral and creates local recording docs ready for processing." size="sm">
                        <Button
                            size="sm"
                            variant="solid"
                            color="primary"
                            startDecorator={<SyncIcon sx={{ fontSize: 14 }} />}
                            loading={fetching}
                            onClick={() => onFetch(account.account_id).then(() => load())}
                        >
                            Pull Call records via API
                        </Button>
                    </Tooltip>
                    <Tooltip title="Disconnect account and revoke OAuth tokens" size="sm">
                        <Button
                            size="sm"
                            variant="outlined"
                            color="danger"
                            startDecorator={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                            onClick={() => onDisconnect(account.account_id)}
                        >
                            Disconnect
                        </Button>
                    </Tooltip>
                    <IconButton size="sm" variant="outlined" onClick={load} disabled={loading}>
                        <RefreshIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Stack>

                {/* cursor + stats strip */}
                <Sheet variant="soft" sx={{ borderRadius: 'sm', p: 1.25 }}>
                    <RecordingsCursorStrip
                        oldestPendingAt={stats?.oldestPendingAt ?? null}
                        lastProcessedAt={stats?.lastProcessedAt ?? null}
                        lastIngestedAt={stats?.lastIngestedAt ?? null}
                    />
                    <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ mt: 1 }}>
                        <FactItem label="Pending" value={String(counts?.pending ?? 0)} />
                        <FactItem label="Processed" value={String(counts?.processed ?? 0)} />
                        <FactItem label="Unattributed" value={String(counts?.unattributed ?? 0)} />
                        <FactItem label="Failed" value={String(counts?.failed ?? 0)} danger={!!counts && counts.failed > 0} />
                        {counts && counts.discovered + counts.transcribing + counts.transcribed + counts.diarizing + counts.ready > 0 && (
                            <FactItem
                                label="In flight"
                                value={String(counts.discovered + counts.transcribing + counts.transcribed + counts.diarizing + counts.ready)}
                            />
                        )}
                        <FactItem label="Last poll" value={lastPollIso ? relTime(lastPollIso) : 'never'} />
                        {stateDoc?.last_poll_cursor && (
                            <Stack direction="row" gap={0.5} alignItems="baseline">
                                <Typography level="body-xs" color="neutral" sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    Cursor
                                </Typography>
                                <Typography
                                    level="body-xs"
                                    sx={{
                                        fontFamily: 'ui-monospace, Menlo, monospace',
                                        fontWeight: 'md',
                                        maxWidth: 220,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {stateDoc.last_poll_cursor}
                                </Typography>
                            </Stack>
                        )}
                    </Stack>
                </Sheet>

                {/* status filter chips */}
                <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap">
                    <Typography level="body-xs" color="neutral">
                        Status:
                    </Typography>
                    <Chip size="sm" variant={filter === 'all' ? 'solid' : 'soft'} color="neutral" onClick={() => setFilter('all')} sx={{ cursor: 'pointer' }}>
                        all · {recordings.length}
                    </Chip>
                    {ALL_RECORDING_STATUSES.map((s) => {
                        const n = counts?.[s] ?? 0
                        if (n === 0 && filter !== s) return null
                        return (
                            <Chip
                                key={s}
                                size="sm"
                                variant={filter === s ? 'solid' : 'soft'}
                                color={recordingStatusColor(s)}
                                onClick={() => setFilter(s)}
                                sx={{ cursor: 'pointer' }}
                            >
                                {s} · {n}
                            </Chip>
                        )
                    })}
                </Stack>

                {error && (
                    <Alert
                        color="danger"
                        size="sm"
                        endDecorator={
                            <IconButton size="sm" variant="plain" color="danger" onClick={() => setError(null)}>
                                ×
                            </IconButton>
                        }
                    >
                        {error}
                    </Alert>
                )}
                {loading && <LinearProgress />}

                {/* recordings list */}
                <Box sx={{ maxHeight: '55vh', minHeight: 220, overflow: 'auto', pr: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {visiblePage.length === 0 && !loading ? (
                        <Sheet variant="soft" sx={{ p: 3, borderRadius: 'sm', textAlign: 'center' }}>
                            <Typography level="body-sm" color="neutral">
                                {recordings.length === 0
                                    ? 'No recordings yet. Use Sync to pull recent calls from RingCentral.'
                                    : 'No recordings match this filter.'}
                            </Typography>
                        </Sheet>
                    ) : (
                        visiblePage.map((rec) => (
                            <RecordingRow
                                key={rec.recording_id}
                                rec={rec}
                                busy={busyId === rec.recording_id}
                                onSelect={() => setSelected(rec)}
                                onProcess={() => handleAction('process', rec.recording_id)}
                                onForceProcess={() => handleAction('force', rec.recording_id)}
                                onRequeue={() => handleAction('requeue', rec.recording_id)}
                                onDelete={() => handleAction('delete', rec.recording_id)}
                            />
                        ))
                    )}
                </Box>

                {/* pagination */}
                {filtered.length > PAGE_SIZE && (
                    <Stack direction="row" gap={1} justifyContent="center" alignItems="center" sx={{ pt: 0.5 }}>
                        <Button size="sm" variant="outlined" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                            Newer
                        </Button>
                        <Typography level="body-xs" color="neutral" sx={{ minWidth: 80, textAlign: 'center' }}>
                            Page {page + 1} / {pageCount}
                        </Typography>
                        <Button size="sm" variant="outlined" disabled={page >= pageCount - 1} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}>
                            Older
                        </Button>
                    </Stack>
                )}

                {/* re-auth shortcut */}
                {account.status === 'oauth_expired' && (
                    <Alert
                        size="sm"
                        color="warning"
                        endDecorator={
                            <Button size="sm" variant="solid" color="warning" loading={connecting} onClick={onConnect}>
                                Re-authorize
                            </Button>
                        }
                    >
                        OAuth tokens have expired. Re-authorize to resume fetching.
                    </Alert>
                )}
            </Stack>

            {selected && (
                <RecordingDetailModal
                    rec={selected}
                    busy={busyId === selected.recording_id}
                    onClose={() => setSelected(null)}
                    onProcess={() => handleAction('process', selected.recording_id)}
                    onForceProcess={() => handleAction('force', selected.recording_id)}
                    onRequeue={() => handleAction('requeue', selected.recording_id)}
                    onDelete={() => handleAction('delete', selected.recording_id)}
                />
            )}
        </Sheet>
    )
}

// ─── recordings cursor strip (visual timeline) ───────────────────────────────

function RecordingsCursorStrip({
    oldestPendingAt,
    lastProcessedAt,
    lastIngestedAt,
}: {
    oldestPendingAt: string | null
    lastProcessedAt: string | null
    lastIngestedAt: string | null
}): JSX.Element {
    if (!oldestPendingAt && !lastProcessedAt && !lastIngestedAt) {
        return (
            <Typography level="body-xs" color="neutral" sx={{ fontStyle: 'italic' }}>
                no recordings yet — click Sync to pull recent calls
            </Typography>
        )
    }

    // Lag = time since last ingestion; > 1h = warning.
    const lagSec = lastIngestedAt ? Math.max(0, Math.floor((Date.now() - new Date(lastIngestedAt).getTime()) / 1000)) : null
    const showOldest = !!oldestPendingAt && oldestPendingAt !== lastProcessedAt

    return (
        <Stack direction="row" alignItems="center" gap={1}>
            <Box sx={{ position: 'relative', flex: 1, height: 14, display: 'flex', alignItems: 'center' }}>
                <Box sx={{ position: 'absolute', left: 0, right: 0, top: '50%', height: '1px', bgcolor: 'divider' }} />
                {showOldest && (
                    <Tooltip title={`Oldest pending: ${new Date(oldestPendingAt!).toLocaleString()}`} size="sm">
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '4%',
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: 'warning.500',
                                boxShadow: '0 0 0 2px var(--joy-palette-background-surface)',
                            }}
                        />
                    </Tooltip>
                )}
                {lastProcessedAt && (
                    <Tooltip title={`Last processed: ${new Date(lastProcessedAt).toLocaleString()}`} size="sm">
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 0,
                                height: 0,
                                borderLeft: '6px solid transparent',
                                borderRight: '6px solid transparent',
                                borderBottom: '10px solid var(--joy-palette-success-500)',
                            }}
                        />
                    </Tooltip>
                )}
                {lastIngestedAt && (
                    <Tooltip title={`Last ingested: ${new Date(lastIngestedAt).toLocaleString()}`} size="sm">
                        <Box
                            sx={{
                                position: 'absolute',
                                right: '4%',
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: 'primary.500',
                                boxShadow: '0 0 0 2px var(--joy-palette-background-surface)',
                            }}
                        />
                    </Tooltip>
                )}
            </Box>
            <Typography
                level="body-xs"
                sx={{ fontFamily: 'ui-monospace, Menlo, monospace', minWidth: 80, textAlign: 'right' }}
                color={lagSec != null && lagSec > 3600 ? 'danger' : 'neutral'}
            >
                {lagSec == null ? '—' : lagSec === 0 ? '✓ live' : `lag −${fmtLag(lagSec)}`}
            </Typography>
        </Stack>
    )
}

function fmtLag(sec: number): string {
    if (sec < 60) return `${sec}s`
    const m = Math.floor(sec / 60)
    if (m < 60) return `${m}m`
    const h = Math.floor(m / 60)
    if (h < 24) return `${h}h${m % 60 ? ` ${m % 60}m` : ''}`
    return `${Math.floor(h / 24)}d`
}

// ─── recording row ───────────────────────────────────────────────────────────

// ─── custom audio player ─────────────────────────────────────────────────────

const SPEEDS: Array<1 | 1.5 | 2> = [1, 1.5, 2]

function fmtTime(s: number): string {
    if (!Number.isFinite(s) || s < 0) return '0:00'
    const m = Math.floor(s / 60)
    return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`
}

function AudioPlayer({ recordingId, hasAudio, size = 'row' }: { recordingId: string; hasAudio: boolean; size?: 'row' | 'modal' }): JSX.Element {
    const wsId = workspaceId()
    const audioRef = useRef<HTMLAudioElement | null>(null)
    const [audioUrl, setAudioUrl] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [playing, setPlaying] = useState(false)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(0)
    const [speedIdx, setSpeedIdx] = useState(0)

    useEffect(
        () => () => {
            if (audioUrl) URL.revokeObjectURL(audioUrl)
        },
        [audioUrl]
    )

    const handleFetch = useCallback(async () => {
        if (audioUrl || loading) return
        setLoading(true)
        setError(null)
        try {
            const url = await rcApi.getAudioUrl(recordingId, wsId)
            setAudioUrl(url)
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load audio')
        } finally {
            setLoading(false)
        }
    }, [audioUrl, loading, recordingId, wsId])

    const handlePlayPause = useCallback(() => {
        const el = audioRef.current
        if (!el) return
        if (playing) el.pause()
        else void el.play()
    }, [playing])

    const handleSeek = useCallback((_: Event, val: number | number[]) => {
        const el = audioRef.current
        if (!el) return
        const t = Array.isArray(val) ? val[0] : val
        el.currentTime = t
        setCurrentTime(t)
    }, [])

    const handleSpeedCycle = useCallback(() => {
        const nextIdx = (speedIdx + 1) % SPEEDS.length
        setSpeedIdx(nextIdx)
        if (audioRef.current) audioRef.current.playbackRate = SPEEDS[nextIdx]
    }, [speedIdx])

    if (!hasAudio) {
        return (
            <Tooltip title="Process recording first to enable playback" size="sm">
                <span>
                    <IconButton size="sm" variant="plain" color="neutral" disabled>
                        <HeadphonesIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </span>
            </Tooltip>
        )
    }

    if (!audioUrl) {
        return (
            <Tooltip title={error ?? 'Load audio'} size="sm" color={error ? 'danger' : undefined}>
                <IconButton size="sm" variant="plain" color={error ? 'danger' : 'success'} loading={loading} onClick={handleFetch}>
                    <HeadphonesIcon sx={{ fontSize: size === 'modal' ? 18 : 14 }} />
                </IconButton>
            </Tooltip>
        )
    }

    const speed = SPEEDS[speedIdx]

    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={size === 'modal' ? 1 : 0.5}
            sx={{ width: '100%', bgcolor: 'background.level1', borderRadius: 'sm', px: size === 'modal' ? 1.5 : 1, py: 0.5 }}
        >
            {/* hidden audio element — controlled programmatically */}
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <audio
                ref={audioRef}
                src={audioUrl}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onEnded={() => {
                    setPlaying(false)
                    setCurrentTime(0)
                }}
                onTimeUpdate={(e) => setCurrentTime((e.target as HTMLAudioElement).currentTime)}
                onLoadedMetadata={(e) => setDuration((e.target as HTMLAudioElement).duration)}
            />
            <IconButton size="sm" variant="plain" color="primary" onClick={handlePlayPause} sx={{ flexShrink: 0 }}>
                {playing ? <PauseIcon sx={{ fontSize: size === 'modal' ? 20 : 16 }} /> : <PlayArrowIcon sx={{ fontSize: size === 'modal' ? 20 : 16 }} />}
            </IconButton>
            <Typography level="body-xs" sx={{ fontFamily: 'ui-monospace, monospace', flexShrink: 0, minWidth: 28, color: 'text.primary' }}>
                {fmtTime(currentTime)}
            </Typography>
            <Slider
                size="sm"
                min={0}
                max={duration || 1}
                value={currentTime}
                onChange={handleSeek}
                sx={{
                    flex: 1,
                    minWidth: 40,
                    '--Slider-trackSize': '3px',
                    '--Slider-thumbSize': '11px',
                    '--Slider-thumbBackground': 'var(--joy-palette-primary-500)',
                    '--Slider-trackBackground': 'var(--joy-palette-primary-200)',
                    padding: '8px 0',
                }}
            />
            <Typography level="body-xs" sx={{ fontFamily: 'ui-monospace, monospace', flexShrink: 0, minWidth: 28, color: 'text.tertiary' }}>
                {fmtTime(duration)}
            </Typography>
            <Tooltip title="Cycle playback speed" size="sm">
                <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={handleSpeedCycle}
                    sx={{ flexShrink: 0, fontSize: 10, fontFamily: 'ui-monospace, monospace', minWidth: 28, fontWeight: 'bold' }}
                >
                    {speed === 1 ? '1×' : speed === 1.5 ? '1.5×' : '2×'}
                </IconButton>
            </Tooltip>
        </Stack>
    )
}

function RecordingRow({
    rec,
    busy,
    onSelect,
    onProcess,
    onForceProcess,
    onRequeue,
    onDelete,
}: {
    rec: RingCentralRecording
    busy: boolean
    onSelect: () => void
    onProcess: () => void
    onForceProcess: () => void
    onRequeue: () => void
    onDelete: () => void
}): JSX.Element {
    const status = rec.status as RecordingStatus
    const canProcess =
        status === 'discovered' || status === 'transcribing' || status === 'transcribed' || status === 'diarizing' || status === 'ready' || status === 'failed'
    const canRequeue = status === 'processed' || status === 'unattributed' || status === 'failed'

    return (
        <Sheet
            variant="outlined"
            sx={{
                borderRadius: 'sm',
                p: 1,
                borderLeftWidth: 3,
                borderLeftColor: `${recordingStatusColor(status)}.500`,
                transition: 'background-color 120ms',
                '&:hover': { bgcolor: 'background.level1' },
            }}
        >
            <Stack direction="row" gap={1} alignItems="flex-start">
                <Box sx={{ flexShrink: 0, pt: 0.25 }}>
                    <StatusChip status={status} />
                </Box>
                <Chip size="sm" variant="soft" color="neutral" startDecorator={<DirectionIcon direction={rec.direction} />} sx={{ flexShrink: 0 }}>
                    {rec.direction === 'Inbound' ? 'in' : 'out'}
                </Chip>
                <Stack flex={1} minWidth={0} gap={0.25}>
                    <Typography level="body-xs" color="neutral">
                        <strong>{rec.external_party_name || rec.external_party_phone || 'Unknown caller'}</strong>
                        {' · '}
                        {fmtDate(rec.started_at)}
                        {' · '}
                        {fmtDuration(rec.duration)}
                    </Typography>
                    <Typography
                        level="body-sm"
                        sx={{
                            whiteSpace: 'pre-wrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            color: rec.transcription?.text ? undefined : 'text.tertiary',
                            fontStyle: rec.transcription?.text ? undefined : 'italic',
                        }}
                    >
                        {rec.transcription?.text || `(${status} — no transcript yet)`}
                    </Typography>
                </Stack>
                <Stack direction="row" gap={0.25} flexShrink={0} alignItems="center">
                    <AudioPlayer recordingId={rec.recording_id} hasAudio={!!rec.gcs_audio_uri} />
                    <Tooltip title="View details" size="sm">
                        <IconButton size="sm" variant="plain" onClick={onSelect}>
                            <VisibilityIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                    {canProcess ? (
                        <Tooltip title="Process to DataSource boundary (transcribe + diarize + pending)" size="sm">
                            <IconButton size="sm" variant="plain" color="primary" loading={busy} onClick={onProcess}>
                                <PlayArrowIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Tooltip title="Re-run STT + diarize from scratch" size="sm">
                            <IconButton size="sm" variant="plain" color="neutral" loading={busy} onClick={onForceProcess}>
                                <PlayArrowIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {canRequeue && (
                        <Tooltip title="Requeue (reset to pending)" size="sm">
                            <IconButton size="sm" variant="plain" color="primary" loading={busy} onClick={onRequeue}>
                                <ReplayIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Delete (Firestore + GCS)" size="sm">
                        <IconButton size="sm" variant="plain" color="danger" loading={busy} onClick={onDelete}>
                            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>
        </Sheet>
    )
}

// ─── recording detail modal ──────────────────────────────────────────────────

function RecordingDetailModal({
    rec,
    busy,
    onClose,
    onProcess,
    onForceProcess,
    onRequeue,
    onDelete,
}: {
    rec: RingCentralRecording
    busy: boolean
    onClose: () => void
    onProcess: () => void
    onForceProcess: () => void
    onRequeue: () => void
    onDelete: () => void
}): JSX.Element {
    const status = rec.status as RecordingStatus
    const canProcess =
        status === 'discovered' || status === 'transcribing' || status === 'transcribed' || status === 'diarizing' || status === 'ready' || status === 'failed'
    const canRequeue = status === 'processed' || status === 'unattributed' || status === 'failed'
    const [batch, setBatch] = useState<RingCentralSourceBatch | null>(null)

    useEffect(() => {
        setBatch(null)
        rcApi
            .getRecordingBatch(rec.recording_id)
            .then(setBatch)
            .catch(() => null)
    }, [rec.recording_id, rec.status])

    return (
        <Modal open onClose={onClose}>
            <ModalDialog sx={{ maxWidth: 760, width: '90vw' }}>
                <ModalClose />
                {/* header */}
                <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1 }}>
                    <StatusChip status={status} />
                    <Chip size="sm" variant="soft" color="neutral" startDecorator={<DirectionIcon direction={rec.direction} />}>
                        {rec.direction}
                    </Chip>
                    <Typography level="title-md">{rec.external_party_name || rec.external_party_phone || 'Unknown caller'}</Typography>
                </Stack>
                <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ mb: 1 }}>
                    <FactItem label="Started" value={fmtDate(rec.started_at)} />
                    <FactItem label="Dur" value={fmtDuration(rec.duration)} />
                    {rec.external_party_phone && <FactItem label="Phone" value={rec.external_party_phone} />}
                    <Typography level="body-xs" color="neutral" sx={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>
                        {rec.recording_id}
                    </Typography>
                </Stack>

                <Divider sx={{ my: 1 }} />

                {/* audio */}
                <Box sx={{ mb: 1.5 }}>
                    <AudioPlayer recordingId={rec.recording_id} hasAudio={!!rec.gcs_audio_uri} size="modal" />
                </Box>

                {/* transcript — speaker-labelled if batch available, raw fallback */}
                <Typography level="body-xs" color="neutral" sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Transcript
                </Typography>
                <Sheet variant="soft" sx={{ borderRadius: 'sm', p: 1, maxHeight: 300, overflow: 'auto', mb: batch ? 1 : 1.5 }}>
                    {batch ? (
                        <Stack gap={0.25}>
                            {batch.text.split('\n').map((line, i) => {
                                const agentMatch = line.match(/^\[Agent\](.*)/)
                                const extMatch = line.match(/^\[External\](.*)/)
                                if (agentMatch)
                                    return (
                                        <Stack key={i} direction="row" gap={0.75}>
                                            <Chip size="sm" variant="soft" color="primary" sx={{ flexShrink: 0, fontSize: 10 }}>
                                                Agent
                                            </Chip>
                                            <Typography level="body-sm">{agentMatch[1].trim()}</Typography>
                                        </Stack>
                                    )
                                if (extMatch)
                                    return (
                                        <Stack key={i} direction="row" gap={0.75}>
                                            <Chip size="sm" variant="soft" color="warning" sx={{ flexShrink: 0, fontSize: 10 }}>
                                                External
                                            </Chip>
                                            <Typography level="body-sm">{extMatch[1].trim()}</Typography>
                                        </Stack>
                                    )
                                return line ? (
                                    <Typography key={i} level="body-sm">
                                        {line}
                                    </Typography>
                                ) : null
                            })}
                        </Stack>
                    ) : rec.transcription?.text ? (
                        <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                            {rec.transcription.text}
                        </Typography>
                    ) : (
                        <Typography level="body-xs" color="neutral" sx={{ fontStyle: 'italic' }}>
                            No transcript yet — status: {status}.{canProcess && ' Click "Process now" to run STT + diarization.'}
                        </Typography>
                    )}
                </Sheet>

                {/* contacts + attributions from batch */}
                {batch && (batch.hints?.contacts?.length || batch.hints?.attributions?.length) ? (
                    <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mb: 1.5 }}>
                        {batch.hints?.contacts?.map((c, i) => (
                            <FactItem key={i} label="Contact" value={[c.name, c.phone].filter(Boolean).join(' · ') || c.sourceId} />
                        ))}
                        {batch.hints?.attributions?.map((a, i) => <FactItem key={i} label={a.system} value={`${a.nameHint} (${a.externalId})`} />)}
                    </Stack>
                ) : null}

                {/* actions */}
                <Stack direction="row" gap={1} flexWrap="wrap">
                    {canProcess && (
                        <Button
                            size="sm"
                            variant="solid"
                            color="primary"
                            startDecorator={<PlayArrowIcon sx={{ fontSize: 14 }} />}
                            loading={busy}
                            onClick={onProcess}
                        >
                            Process now
                        </Button>
                    )}
                    {!canProcess && (
                        <Button size="sm" variant="outlined" startDecorator={<PlayArrowIcon sx={{ fontSize: 14 }} />} loading={busy} onClick={onForceProcess}>
                            Re-run STT
                        </Button>
                    )}
                    {canRequeue && (
                        <Button
                            size="sm"
                            variant="outlined"
                            color="primary"
                            startDecorator={<ReplayIcon sx={{ fontSize: 14 }} />}
                            loading={busy}
                            onClick={onRequeue}
                        >
                            Requeue
                        </Button>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Button
                        size="sm"
                        variant="outlined"
                        color="danger"
                        startDecorator={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                        loading={busy}
                        onClick={onDelete}
                    >
                        Delete
                    </Button>
                </Stack>
            </ModalDialog>
        </Modal>
    )
}

// ─── main panel ──────────────────────────────────────────────────────────────

export function RingCentralPanel({ onBack }: { onBack: () => void }): JSX.Element {
    const [selectedAccountId, setSelectedAccountId] = useUrlPathSegment(1)
    const [accounts, setAccounts] = useState<RingCentralAccount[]>([])
    const [aggregate, setAggregate] = useState<AggregateCounts>({ pending: 0, processed: 0, unattributed: 0, failed: 0 })
    const [lastIngestedAt, setLastIngestedAt] = useState<string | undefined>(undefined)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [connecting, setConnecting] = useState(false)
    const [fetchingFor, setFetchingFor] = useState<string | null>(null)
    const [refreshing, setRefreshing] = useState(false)
    const [etlOpen, setEtlOpen] = useState(false)
    const [accountsModalOpen, setAccountsModalOpen] = useState(false)
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

    const wsId = workspaceId()

    const loadAccounts = useCallback(
        async (silent = false) => {
            if (!silent) setLoading(true)
            setError(null)
            try {
                const data = await rcApi.listAccounts(wsId)
                // eslint-disable-next-line no-console
                console.log('[RingCentralPanel] listAccounts →', data)
                setAccounts(data)
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('[RingCentralPanel] listAccounts failed', err)
                setError(err instanceof Error ? err.message : 'Failed to load accounts')
            } finally {
                setLoading(false)
                setRefreshing(false)
            }
        },
        [wsId]
    )

    const loadAggregate = useCallback(async (list: RingCentralAccount[]) => {
        if (list.length === 0) {
            setAggregate({ pending: 0, processed: 0, unattributed: 0, failed: 0 })
            return
        }
        try {
            const results = await Promise.all(list.map((a) => rcApi.getAccountStats(a.account_id)))
            const agg: AggregateCounts = { pending: 0, processed: 0, unattributed: 0, failed: 0 }
            let latestSync: string | undefined
            for (let i = 0; i < results.length; i++) {
                const c = results[i].counts
                agg.pending += c.pending
                agg.processed += c.processed
                agg.unattributed += c.unattributed
                agg.failed += c.failed
                const syncedAt = list[i].last_synced_at
                if (syncedAt && (!latestSync || syncedAt > latestSync)) latestSync = syncedAt
            }
            setAggregate(agg)
            setLastIngestedAt(latestSync)
        } catch {
            // Stats failures are non-fatal; leave previous values
        }
    }, [])

    useEffect(() => {
        loadAccounts()
    }, [loadAccounts])

    useEffect(() => {
        loadAggregate(accounts)
    }, [accounts, loadAggregate])

    useEffect(
        () => () => {
            if (pollRef.current) clearInterval(pollRef.current)
        },
        []
    )

    const handleConnect = useCallback(async () => {
        setConnecting(true)
        try {
            const result = await rcApi.initiateOAuth(wsId)
            const authUrl = result?.data?.authUrl
            if (authUrl) {
                window.open(authUrl, '_blank', 'width=640,height=780')
                if (pollRef.current) clearInterval(pollRef.current)
                pollRef.current = setInterval(() => loadAccounts(true), 3000)
                setTimeout(() => {
                    if (pollRef.current) clearInterval(pollRef.current)
                    pollRef.current = null
                }, 120_000)
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to initiate OAuth')
        } finally {
            setConnecting(false)
        }
    }, [loadAccounts, wsId])

    const handleFetch = useCallback(
        async (accountId: string) => {
            setFetchingFor(accountId)
            try {
                await rcApi.fetchRecordings(wsId, accountId)
                await loadAccounts(true)
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Sync failed')
            } finally {
                setFetchingFor(null)
            }
        },
        [loadAccounts, wsId]
    )

    const handleDisconnect = useCallback(
        async (accountId: string) => {
            const acc = accounts.find((a) => a.account_id === accountId)
            const name = acc?.account_name ?? accountId
            if (!confirm(`Disconnect "${name}"? OAuth tokens will be revoked.`)) return
            try {
                await rcApi.deleteAccount(wsId, accountId)
                if (selectedAccountId === accountId) setSelectedAccountId(null)
                await loadAccounts(true)
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Disconnect failed')
            }
        },
        [accounts, loadAccounts, selectedAccountId, setSelectedAccountId, wsId]
    )

    const handleRefresh = useCallback(async () => {
        setRefreshing(true)
        await loadAccounts()
    }, [loadAccounts])

    const activeCount = accounts.filter((a) => a.status === 'active').length
    const selected = useMemo(() => {
        if (!selectedAccountId) return null
        // Defensive String() coercion — historic docs may have numeric account_id.
        const found = accounts.find((a) => String(a.account_id) === String(selectedAccountId)) ?? null
        // eslint-disable-next-line no-console
        console.log('[RingCentralPanel] selected lookup', { selectedAccountId, found: found?.account_id ?? null, count: accounts.length })
        return found
    }, [accounts, selectedAccountId])

    return (
        <Stack gap={1.5}>
            {/* Header */}
            <Stack direction="row" alignItems="center" gap={1}>
                <IconButton size="sm" variant="plain" color="neutral" onClick={onBack}>
                    <ArrowBackIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <PhoneInTalkIcon sx={{ fontSize: 18, color: 'warning.500' }} />
                <Stack gap={0} flex={1}>
                    <Typography level="title-sm">RingCentral</Typography>
                    <Typography level="body-xs" color="neutral">
                        Call transcripts and voicemails from connected accounts
                    </Typography>
                </Stack>
                {!selected && (
                    <>
                        <Chip size="sm" variant="soft" color="warning">
                            {aggregate.pending} pending
                        </Chip>
                        <Chip size="sm" variant="soft" color="success">
                            {aggregate.processed} processed
                        </Chip>
                        <Chip size="sm" variant="soft" color="neutral">
                            {aggregate.unattributed} unattributed
                        </Chip>
                        {aggregate.failed > 0 && (
                            <Chip size="sm" variant="soft" color="danger">
                                {aggregate.failed} failed
                            </Chip>
                        )}
                        {lastIngestedAt && (
                            <Typography level="body-xs" color="neutral">
                                · {relTime(lastIngestedAt)}
                            </Typography>
                        )}
                        <Button size="sm" variant="outlined" startDecorator={<ScienceIcon sx={{ fontSize: 14 }} />} onClick={() => setEtlOpen(true)}>
                            Dry Run ETL
                        </Button>
                    </>
                )}
                <Tooltip title="Refresh" size="sm">
                    <IconButton size="sm" variant="outlined" color="neutral" onClick={handleRefresh} loading={refreshing}>
                        <RefreshIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
                <Button
                    size="sm"
                    variant={accounts.length > 0 ? 'outlined' : 'solid'}
                    color={accounts.length > 0 ? 'neutral' : 'warning'}
                    startDecorator={<PhoneInTalkIcon sx={{ fontSize: 14 }} />}
                    onClick={() => setAccountsModalOpen(true)}
                >
                    Accounts · {activeCount}
                </Button>
            </Stack>

            {error && (
                <Alert
                    size="sm"
                    color="danger"
                    startDecorator={<ErrorOutlineIcon />}
                    endDecorator={
                        <IconButton size="sm" variant="plain" color="danger" onClick={() => setError(null)}>
                            ×
                        </IconButton>
                    }
                >
                    {error}
                </Alert>
            )}

            {/* Body switches between list and detail via URL segment */}
            {selected ? (
                <AccountDetailView
                    account={selected}
                    onBack={() => setSelectedAccountId(null)}
                    onConnect={handleConnect}
                    connecting={connecting}
                    onDisconnect={handleDisconnect}
                    onFetch={handleFetch}
                    fetching={fetchingFor === selected.account_id}
                />
            ) : (
                <>
                    <AccountsListView
                        accounts={accounts}
                        loading={loading}
                        onOpenAccount={setSelectedAccountId}
                        onConnect={handleConnect}
                        connecting={connecting}
                    />

                    {accounts.length === 0 && !loading && (
                        <Sheet variant="soft" color="neutral" sx={{ borderRadius: 'md', p: 2 }}>
                            <Typography level="body-xs" color="neutral" sx={{ mb: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 10 }}>
                                What gets ingested
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                                {[
                                    { icon: <PhoneCallbackIcon sx={{ fontSize: 14 }} />, label: 'Inbound call transcripts' },
                                    { icon: <PhoneForwardedIcon sx={{ fontSize: 14 }} />, label: 'Outbound call transcripts' },
                                    { icon: <MicIcon sx={{ fontSize: 14 }} />, label: 'Voicemails' },
                                ].map((f) => (
                                    <Stack key={f.label} direction="row" alignItems="center" gap={1} sx={{ flex: 1 }}>
                                        <Box sx={{ color: 'text.secondary' }}>{f.icon}</Box>
                                        <Typography level="body-xs">{f.label}</Typography>
                                    </Stack>
                                ))}
                            </Stack>
                            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
                                {[
                                    'discovered → fetched from RC API',
                                    'transcribing → Vertex STT',
                                    'pending → at DataSource boundary',
                                    'processed → committed',
                                ].map((h) => (
                                    <Chip
                                        key={h}
                                        size="sm"
                                        variant="plain"
                                        color="neutral"
                                        startDecorator={<HourglassEmptyIcon sx={{ fontSize: 10 }} />}
                                        sx={{ fontSize: 9 }}
                                    >
                                        {h}
                                    </Chip>
                                ))}
                            </Stack>
                        </Sheet>
                    )}
                </>
            )}

            {/* artifacts */}
            <AccountsModal
                open={accountsModalOpen}
                onClose={() => setAccountsModalOpen(false)}
                accounts={accounts}
                connecting={connecting}
                onConnect={handleConnect}
                onOpenAccount={(id) => {
                    setSelectedAccountId(id)
                    setAccountsModalOpen(false)
                }}
                onDisconnect={handleDisconnect}
            />
            <EtlPreviewModal open={etlOpen} onClose={() => setEtlOpen(false)} onRunPreview={(limit) => rcApi.previewBatch(limit)} />
        </Stack>
    )
}

// ─── Accounts modal ──────────────────────────────────────────────────────────

function AccountsModal({
    open,
    onClose,
    accounts,
    connecting,
    onConnect,
    onOpenAccount,
    onDisconnect,
}: {
    open: boolean
    onClose: () => void
    accounts: RingCentralAccount[]
    connecting: boolean
    onConnect: () => void
    onOpenAccount: (id: string) => void
    onDisconnect: (id: string) => Promise<void>
}): JSX.Element {
    const activeCount = accounts.filter((a) => a.status === 'active').length

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{ maxWidth: 680, width: '90vw' }}>
                <ModalClose />
                <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                    <PhoneInTalkIcon sx={{ fontSize: 18, color: 'warning.500' }} />
                    <Typography level="title-md">RingCentral accounts</Typography>
                    <Chip size="sm" variant="soft" color={activeCount > 0 ? 'success' : 'neutral'}>
                        {activeCount} of {accounts.length} active
                    </Chip>
                </Stack>
                <Typography level="body-xs" color="neutral" sx={{ mb: 1.5 }}>
                    Manage connected RingCentral accounts. Each account has its own OAuth tokens, polling cursor, and recording set.
                </Typography>

                {accounts.length === 0 ? (
                    <Sheet variant="soft" sx={{ p: 3, borderRadius: 'sm', textAlign: 'center', mb: 1.5 }}>
                        <Typography level="body-sm" color="neutral" sx={{ mb: 1.5 }}>
                            No accounts connected yet.
                        </Typography>
                        <Button
                            size="sm"
                            variant="solid"
                            color="warning"
                            startDecorator={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                            loading={connecting}
                            onClick={onConnect}
                        >
                            Connect RingCentral
                        </Button>
                    </Sheet>
                ) : (
                    <Stack gap={0.75} sx={{ mb: 1.5, maxHeight: '50vh', overflow: 'auto' }}>
                        {accounts.map((a) => (
                            <Sheet
                                key={a.account_id}
                                variant="outlined"
                                sx={{
                                    borderRadius: 'sm',
                                    p: 1.25,
                                    transition: 'background-color 120ms',
                                    '&:hover': { bgcolor: 'background.level1' },
                                }}
                            >
                                <Stack direction="row" alignItems="center" gap={1.5}>
                                    <Sheet
                                        variant="soft"
                                        color={accountStatusColor(a.status)}
                                        sx={{
                                            borderRadius: '50%',
                                            width: 30,
                                            height: 30,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}
                                    >
                                        <PhoneInTalkIcon sx={{ fontSize: 14 }} />
                                    </Sheet>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 0.25 }}>
                                            <AccountStatusChip status={a.status} />
                                            <Typography
                                                level="body-sm"
                                                fontWeight="md"
                                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                            >
                                                {a.account_name || `Account #${a.account_id}`}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" gap={1.5} flexWrap="wrap">
                                            <FactItem label="Last synced" value={relTime(a.last_synced_at)} />
                                            {a.connected_at && <FactItem label="Connected" value={relTime(a.connected_at)} />}
                                            {a.last_error && <FactItem label="Error" value={a.last_error} danger />}
                                        </Stack>
                                    </Box>
                                    <Button size="sm" variant="outlined" color="neutral" onClick={() => onOpenAccount(String(a.account_id))}>
                                        Open
                                    </Button>
                                    <Tooltip title="Disconnect and revoke OAuth tokens" size="sm">
                                        <IconButton size="sm" variant="plain" color="danger" onClick={() => void onDisconnect(String(a.account_id))}>
                                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Sheet>
                        ))}
                    </Stack>
                )}

                <Stack direction="row" gap={1} justifyContent="flex-end">
                    <Button
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        startDecorator={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                        loading={connecting}
                        onClick={onConnect}
                    >
                        {accounts.length === 0 ? 'Connect RingCentral' : 'Add another account'}
                    </Button>
                </Stack>
            </ModalDialog>
        </Modal>
    )
}
